/**
 * Invoice Type UI Metadata
 * 
 * FRONTEND UI METADATA ONLY - Contains display information for invoice types.
 * 
 * STRICT RULES:
 * - Frontend MUST NOT compute tax values or infer business meaning
 * - Frontend MUST NOT generate invoice numbers
 * - Field visibility MUST come from backend UI flags, not inferred
 * - Default values MUST come from backend, not calculated
 * 
 * Backend is the SINGLE SOURCE OF TRUTH for all business logic.
 * Frontend fetches config from: GET /api/invoice-types/{typeId}
 */

// ============================================================================
// UI METADATA (Frontend Only)
// ============================================================================

/**
 * UI metadata for invoice types
 * Contains ONLY display information - NO business rules
 */
export const INVOICE_TYPES_UI = {
  freelancer: {
    id: 'freelancer',
    name: 'Freelancer Invoice',
    description: 'Simple invoices for freelancers with hourly or project-based billing',
    icon: 'Laptop',
    color: 'blue',
    features: ['Hourly & project billing', 'No GST complexity'],
    theme: {
      primaryColor: '#3b82f6',
      accentColor: '#1d4ed8',
      headerBg: '#eff6ff',
      borderColor: '#bfdbfe',
      badgeColor: '#dbeafe',
    },
  },

  coach: {
    id: 'coach',
    name: 'Coach / Consultant Invoice',
    description: 'Session-based billing with packages and retainer support',
    icon: 'Users',
    color: 'purple',
    features: ['Session-based billing', 'Package & retainer support'],
    theme: {
      primaryColor: '#8b5cf6',
      accentColor: '#6d28d9',
      headerBg: '#f5f3ff',
      borderColor: '#c4b5fd',
      badgeColor: '#ede9fe',
    },
  },

  gst: {
    id: 'gst',
    name: 'GST Invoice (India)',
    description: 'GST-compliant invoices with CGST, SGST, and IGST support',
    icon: 'FileText',
    color: 'green',
    features: ['CGST / SGST / IGST', 'Compliance-ready format'],
    theme: {
      primaryColor: '#059669',
      accentColor: '#047857',
      headerBg: '#ecfdf5',
      borderColor: '#6ee7b7',
      badgeColor: '#d1fae5',
    },
  },

  vat: {
    id: 'vat',
    name: 'VAT Invoice',
    description: 'VAT invoices with tax registration numbers and reverse charge support for cross-border B2B transactions',
    icon: 'Percent',
    color: 'orange',
    features: ['VAT registration numbers', 'B2B reverse charge', 'Global VAT rates'],
    theme: {
      primaryColor: '#f97316',
      accentColor: '#ea580c',
      headerBg: '#fff7ed',
      borderColor: '#fed7aa',
      badgeColor: '#ffedd5',
    },
  },

  international: {
    id: 'international',
    name: 'International Invoice',
    description: 'Multi-currency invoices for global clients',
    icon: 'Globe',
    color: 'indigo',
    features: ['Multi-currency support', 'Export-friendly layout'],
    theme: {
      primaryColor: '#6366f1',
      accentColor: '#4f46e5',
      headerBg: '#eef2ff',
      borderColor: '#a5b4fc',
      badgeColor: '#e0e7ff',
    },
  },
};

// ============================================================================
// UI METADATA ACCESSORS
// ============================================================================

/**
 * Get UI metadata for an invoice type
 * For display purposes only - NOT for business logic
 */
export const getInvoiceTypeUI = (typeId) => {
  return INVOICE_TYPES_UI[typeId] || INVOICE_TYPES_UI.freelancer;
};

/**
 * Get all invoice types for display (home page cards)
 */
export const getAllInvoiceTypes = () => {
  return Object.values(INVOICE_TYPES_UI);
};

// ============================================================================
// BACKEND CONFIG MERGING
// ============================================================================

/**
 * Merge backend config with frontend UI metadata
 * Backend controls behavior, UI metadata controls appearance only
 */
export const mergeConfigWithUI = (backendConfig, typeId) => {
  const uiMeta = getInvoiceTypeUI(typeId);
  
  return {
    // Backend controls all behavior (single source of truth)
    ...backendConfig,
    
    // UI metadata for display only
    name: uiMeta.name,
    description: uiMeta.description,
    icon: uiMeta.icon,
    color: uiMeta.color,
    features: uiMeta.features,
    
    // Theme: backend overrides frontend defaults
    theme: {
      ...uiMeta.theme,
      ...(backendConfig?.theme || {}),
      primaryColor: backendConfig?.theme?.primary_color || uiMeta.theme.primaryColor,
      accentColor: backendConfig?.theme?.accent_color || uiMeta.theme.accentColor,
      headerBg: backendConfig?.theme?.header_bg || uiMeta.theme.headerBg,
      borderColor: backendConfig?.theme?.border_color || uiMeta.theme.borderColor,
    },
  };
};

// ============================================================================
// FIELD VISIBILITY (Backend-Driven)
// ============================================================================

/**
 * Get field visibility flags from backend config
 * 
 * IMPORTANT: Reads ONLY from backendConfig.ui.* flags
 * Does NOT infer visibility from field names or enabled_fields
 * 
 * Expected backend UI flags:
 * - show_gst_section, show_gstin, show_cgst, show_sgst, show_igst
 * - show_tax_rate, show_discount, show_po_number, show_currency
 * - show_notes, show_terms, show_bank_details, show_place_of_supply
 */
export const getFieldVisibility = (backendConfig) => {
  // Read directly from backend UI flags - no inference
  const ui = backendConfig?.ui || {};
  
  return {
    // GST section visibility - from backend flags only
    showGST: ui.show_gst_section ?? false,
    showGSTIN: ui.show_gstin ?? false,
    gstin: ui.show_gstin ?? false,
    showCGST: ui.show_cgst ?? false,
    showSGST: ui.show_sgst ?? false,
    showIGST: ui.show_igst ?? false,

    // VAT section visibility
    showVATSection: ui.show_vat_section ?? false,
    showVATNumber: ui.show_vat_number ?? false,
    showReverseCharge: ui.show_reverse_charge ?? false,

    // Standard fields visibility
    showTaxRate: ui.show_tax_rate ?? true,
    showDiscount: ui.show_discount ?? true,
    showPONumber: ui.show_po_number ?? false,
    showCurrency: ui.show_currency ?? true,
    showNotes: ui.show_notes ?? true,
    showTerms: ui.show_terms ?? true,
    showBankDetails: ui.show_bank_details ?? false,
    showPlaceOfSupply: ui.show_place_of_supply ?? false,
  };
};

// ============================================================================
// LABELS (Backend-Driven)
// ============================================================================

/**
 * Get labels from backend config
 * Uses backend values directly, with minimal fallbacks
 */
export const getLabels = (backendConfig) => {
  const labels = backendConfig?.labels || {};
  
  return {
    title: labels.title ?? 'Invoice',
    businessSection: labels.business_section ?? 'From',
    clientSection: labels.client_section ?? 'Bill To',
    itemDescription: labels.item_description ?? 'Description',
    quantity: labels.quantity ?? 'Qty',
    rate: labels.rate ?? 'Rate',
    notesPlaceholder: labels.notes_placeholder ?? '',
    termsPlaceholder: labels.terms_placeholder ?? '',
  };
};

// ============================================================================
// CURRENCY CONFIG (Backend-Driven)
// ============================================================================

/**
 * Get currency configuration from backend
 * Returns backend values directly
 */
export const getCurrencyConfig = (backendConfig) => {
  const currency = backendConfig?.currency || {};
  
  return {
    default: currency.default ?? null,
    options: currency.options ?? [],
    locked: currency.locked ?? false,
  };
};

// ============================================================================
// TAX CONFIG (Backend-Driven)
// ============================================================================

/**
 * Get tax configuration from backend
 * 
 * IMPORTANT: Frontend does NOT compute tax values
 * Only reads configuration for display purposes
 */
export const getTaxConfig = (backendConfig) => {
  const taxRules = backendConfig?.tax_rules || {};
  
  return {
    enabled: taxRules.enabled ?? false,
    type: taxRules.type ?? null,
    label: taxRules.label ?? 'Tax',
    // Read default rate but do NOT use for calculations
    defaultRate: taxRules.default_rate ?? null,
    rates: taxRules.rates ?? null,
  };
};

// ============================================================================
// DEFAULT FORM DATA (Backend-Driven)
// ============================================================================

/**
 * Create default form data from backend config
 * 
 * STRICT RULES:
 * - NO tax calculations (no CGST/SGST split)
 * - NO invoice number generation (backend provides)
 * - NO country inference from currency
 * - Read defaults directly from backendConfig.defaults
 */
export const getDefaultFormData = (typeId, backendConfig) => {
  // Read defaults directly from backend - no calculations
  const defaults = backendConfig?.defaults || {};
  const ui = backendConfig?.ui || {};
  
  // Base form data - all values from backend or empty
  const formData = {
    // Invoice type
    invoice_type: typeId,
    
    // Invoice number - NOT generated by frontend
    // Backend will provide final invoice number
    invoice_number: defaults.invoice_number ?? '',
    
    // Dates - use backend defaults or current date
    invoice_date: defaults.invoice_date ?? new Date().toISOString().split('T')[0],
    due_date: defaults.due_date ?? '',
    
    // Business fields - all empty, user fills
    business_name: defaults.business_name ?? '',
    business_email: defaults.business_email ?? '',
    business_phone: defaults.business_phone ?? '',
    business_address: defaults.business_address ?? '',
    business_city: defaults.business_city ?? '',
    business_state: defaults.business_state ?? '',
    business_zip: defaults.business_zip ?? '',
    business_country: defaults.business_country ?? '',
    business_logo: '',
    
    // Client fields - all empty, user fills
    client_name: defaults.client_name ?? '',
    client_email: defaults.client_email ?? '',
    client_phone: defaults.client_phone ?? '',
    client_address: defaults.client_address ?? '',
    client_city: defaults.client_city ?? '',
    client_state: defaults.client_state ?? '',
    client_zip: defaults.client_zip ?? '',
    client_country: defaults.client_country ?? '',
    
    // PO Number
    po_number: defaults.po_number ?? '',
    
    // Line items - start with one empty row
    items: [{ description: '', quantity: 1, rate: 0, amount: 0 }],
    
    // Currency - from backend defaults only
    currency: defaults.currency ?? '',
    
    // Tax rate - from backend defaults, NO calculations
    tax_rate: defaults.tax_rate ?? null,
    
    // Discount
    discount: defaults.discount ?? 0,
    
    // Notes & Terms - from backend defaults
    notes: defaults.notes ?? '',
    terms: defaults.terms ?? '',
  };
  
  // GST fields - only if backend UI flags indicate visibility
  // Values come DIRECTLY from backend defaults - NO calculations
  if (ui.show_gst_section) {
    formData.business_gstin = defaults.business_gstin ?? '';
    formData.client_gstin = defaults.client_gstin ?? '';
    formData.place_of_supply = defaults.place_of_supply ?? '';
    formData.is_same_state = defaults.is_same_state ?? true;
    // Read rates directly - NO division or calculation
    formData.cgst_rate = defaults.cgst_rate ?? null;
    formData.sgst_rate = defaults.sgst_rate ?? null;
    formData.igst_rate = defaults.igst_rate ?? null;
  }
  
  // VAT fields - only if backend UI flags indicate visibility
  if (ui.show_vat_section) {
    formData.business_vat_number = defaults.business_vat_number ?? '';
    formData.client_vat_number = defaults.client_vat_number ?? '';
    formData.is_reverse_charge = defaults.is_reverse_charge ?? false;
  }

  // Bank details - only if backend UI flags indicate visibility
  if (ui.show_bank_details) {
    formData.bank_name = defaults.bank_name ?? '';
    formData.bank_account = defaults.bank_account ?? '';
    formData.bank_swift = defaults.bank_swift ?? '';
    formData.bank_iban = defaults.bank_iban ?? '';
  }

  return formData;
};

// ============================================================================
// LEGACY COMPATIBILITY
// ============================================================================

/**
 * @deprecated Use getInvoiceTypeUI for UI metadata
 * @deprecated Use backend API for behavior config
 * Kept for backward compatibility only
 */
export const getInvoiceTypeConfig = (typeId) => {
  if (process.env.NODE_ENV === 'development') {
    console.warn(
      '[DEPRECATED] getInvoiceTypeConfig is deprecated. ' +
      'Use getInvoiceTypeUI for UI metadata or fetch from backend API for behavior.'
    );
  }
  return getInvoiceTypeUI(typeId);
};

export default INVOICE_TYPES_UI;
